/**
 * FAQ Page
 * Frequently asked questions with category segregation and search
 * iOS-first design with smooth animations
 * 
 * @author Varun Pandey
 */

import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Search, ChevronDown, ChevronUp, X, HelpCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { faqData, getFAQsByCategory, getCategories, type FAQ } from '../../constants/faqData';
import { cn } from '../../utils/cn';

export const FAQPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const faqRefs = useRef<Record<number, HTMLDivElement | null>>({});

  // Get FAQ ID from URL hash if present (for deep linking)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const faqId = parseInt(hash, 10);
      if (!isNaN(faqId)) {
        const faq = faqData.find(f => f.id === faqId);
        if (faq) {
          setExpandedFAQ(faqId);
          setExpandedCategory(faq.name);
          // Scroll to FAQ after a short delay to ensure it's rendered
          setTimeout(() => {
            faqRefs.current[faqId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 300);
        }
      }
    }
  }, [location.hash]);

  // Filter FAQs based on search query
  const filteredFAQs = useMemo(() => {
    if (!searchQuery.trim()) {
      return faqData;
    }

    const query = searchQuery.toLowerCase().trim();
    return faqData.filter(faq =>
      faq.content.toLowerCase().includes(query) ||
      faq.value.toLowerCase().includes(query) ||
      faq.name.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Group filtered FAQs by category
  const faqsByCategory = useMemo(() => {
    return getFAQsByCategory(filteredFAQs);
  }, [filteredFAQs]);

  const categories = useMemo(() => {
    return getCategories(filteredFAQs);
  }, [filteredFAQs]);

  // Expand all categories when searching
  useEffect(() => {
    if (searchQuery.trim()) {
      categories.forEach(cat => {
        if (!expandedCategory || expandedCategory !== cat) {
          setExpandedCategory(cat);
        }
      });
    }
  }, [searchQuery, categories]);

  const handleFAQClick = (faq: FAQ) => {
    if (expandedFAQ === faq.id) {
      setExpandedFAQ(null);
    } else {
      setExpandedFAQ(faq.id);
      setExpandedCategory(faq.name);
      // Update URL hash for deep linking
      navigate(`/faq#${faq.id}`, { replace: true });
    }
  };

  const handleCategoryToggle = (category: string) => {
    if (expandedCategory === category) {
      setExpandedCategory(null);
      setExpandedFAQ(null);
    } else {
      setExpandedCategory(category);
    }
  };

  const clearSearch = () => {
    setSearchQuery('');
    setExpandedFAQ(null);
    setExpandedCategory(null);
    if (searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  return (
    <div className="w-full min-h-screen bg-[#0B0B0D] text-white">
      <div className="w-full md:px-4 py-10 pb-24">
        {/* Header */}
        <div className="mb-6 lg:mb-8">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="h-8 w-8 lg:h-10 lg:w-10 text-[#8E8E93]" />
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight tracking-wide">Frequently Asked Questions</h1>
          </div>
          <p className="text-[#8E8E93] text-sm lg:text-base">
            Find answers to common questions about StarClinch
          </p>
        </div>

        {/* Search Bar */}
        <div className="mb-6 lg:mb-8">
          <div className="relative">
            <div className="absolute left-4 top-1/2 -translate-y-1/2">
              <Search className="h-5 w-5 text-[#8E8E93]" />
            </div>
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search FAQs..."
              className="w-full pl-12 pr-12 py-3.5 lg:py-4 bg-[#1C1C1E] border border-[#3A3A3C] rounded-xl text-white placeholder-[#8E8E93] focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent transition-all text-base lg:text-lg"
            />
            {searchQuery && (
              <button
                onClick={clearSearch}
                className="absolute right-4 top-1/2 -translate-y-1/2 p-1 rounded-lg hover:bg-[#2C2C2E] transition-colors"
                aria-label="Clear search"
              >
                <X className="h-4 w-4 text-[#8E8E93]" />
              </button>
            )}
          </div>
          {searchQuery && (
            <p className="mt-3 text-sm text-[#8E8E93]">
              Found {filteredFAQs.length} {filteredFAQs.length === 1 ? 'result' : 'results'}
            </p>
          )}
        </div>

        {/* FAQ Categories */}
        {categories.length === 0 ? (
          <div className="text-center py-12 lg:py-16">
            <HelpCircle className="h-16 w-16 text-[#8E8E93] mx-auto mb-4 opacity-50" />
            <p className="text-[#8E8E93] text-lg">No FAQs found matching your search</p>
            <button
              onClick={clearSearch}
              className="mt-4 text-[#F33363] hover:text-[#E02E57] transition-colors text-sm font-medium"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-4 lg:space-y-6">
            {categories.map((category) => {
              const categoryFAQs = faqsByCategory[category];
              const isCategoryExpanded = expandedCategory === category;

              return (
                <motion.div
                  key={category}
                  initial={false}
                  className="bg-[#1C1C1E] rounded-2xl border border-[#3A3A3C] overflow-hidden"
                >
                  {/* Category Header */}
                  <button
                    onClick={() => handleCategoryToggle(category)}
                    className="w-full flex items-center justify-between p-4 lg:p-6 hover:bg-[#252527] transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 lg:w-12 lg:h-12 rounded-full bg-[#3A3A3C] flex items-center justify-center">
                        <HelpCircle className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
                      </div>
                      <div className="text-left">
                        <h2 className="text-lg lg:text-xl font-semibold text-white">{category}</h2>
                        <p className="text-xs lg:text-sm text-[#8E8E93]">
                          {categoryFAQs.length} {categoryFAQs.length === 1 ? 'question' : 'questions'}
                        </p>
                      </div>
                    </div>
                    {isCategoryExpanded ? (
                      <ChevronUp className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93] flex-shrink-0" />
                    ) : (
                      <ChevronDown className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93] flex-shrink-0" />
                    )}
                  </button>

                  {/* Category FAQs */}
                  <AnimatePresence>
                    {isCategoryExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pt-4 lg:px-6 pb-4 lg:pb-6 space-y-3 lg:space-y-4">
                          {categoryFAQs.map((faq, index) => {
                            const isExpanded = expandedFAQ === faq.id;
                            const isHighlighted = searchQuery.trim() && (
                              faq.content.toLowerCase().includes(searchQuery.toLowerCase()) ||
                              faq.value.toLowerCase().includes(searchQuery.toLowerCase())
                            );

                            return (
                              <motion.div
                                key={faq.id}
                                ref={(el) => {
                                  faqRefs.current[faq.id] = el;
                                }}
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                                className={cn(
                                  "bg-[#252527] rounded-xl border transition-all",
                                  isExpanded
                                    ? "border-[#3A3A3C] shadow-lg"
                                    : "border-[#3A3A3C]/50 hover:border-[#3A3A3C]",
                                  isHighlighted && "ring-2 ring-[#3A3A3C]/50"
                                )}
                              >
                                {/* Question */}
                                <button
                                  onClick={() => handleFAQClick(faq)}
                                  className="w-full flex items-start justify-between gap-4 p-4 lg:p-5 text-left hover:bg-[#2C2C2E] transition-colors rounded-xl"
                                >
                                  <span className="flex-1 text-base lg:text-lg font-medium text-white pr-2">
                                    {faq.content}
                                  </span>
                                  <div className="flex-shrink-0 pt-1">
                                    {isExpanded ? (
                                      <ChevronUp className="h-5 w-5 text-[#8E8E93]" />
                                    ) : (
                                      <ChevronDown className="h-5 w-5 text-[#8E8E93]" />
                                    )}
                                  </div>
                                </button>

                                {/* Answer */}
                                <AnimatePresence>
                                  {isExpanded && (
                                    <motion.div
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: 'auto', opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1.0] }}
                                      className="overflow-hidden"
                                    >
                                      <div className="px-4 lg:px-5 pb-4 lg:pb-5 pt-0">
                                        <div className="pt-4 border-t border-[#3A3A3C]/50">
                                          <p className="text-sm lg:text-base text-[#8E8E93] leading-relaxed whitespace-pre-line">
                                            {faq.value}
                                          </p>
                                        </div>
                                      </div>
                                    </motion.div>
                                  )}
                                </AnimatePresence>
                              </motion.div>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* Footer Help Text */}
        {!searchQuery && (
          <div className="mt-8 lg:mt-12 text-center">
            <p className="text-[#8E8E93] text-sm lg:text-base">
              Still have questions?{' '}
              <a
                href="/contact-us"
                className="text-[#F33363] hover:text-[#E02E57] transition-colors font-medium"
              >
                Contact us
              </a>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
